/**
 * traits.js
 * ─────────
 * The base block style model.
 *
 * Every block instance is `{ id, type, props, style }`.
 *   - `props` holds the block's own data (config): text, source, counts…
 *   - `style` holds trait values. A trait is a named group of style
 *     controls that any block can enable. Traits render in ONE fixed
 *     order on every block, so the sidebar is the same everywhere.
 *
 * The trait list a block enables lives in blockDefinitions.json
 * (`traits: ["spacing", "visibility"]`). This module owns:
 *   - TRAIT_ORDER        the fixed sidebar order
 *   - TRAIT_DEFS         labels, icons, search keywords per trait
 *   - SPACING_SCALE      the five step spacing tokens (desktop + mobile)
 *   - resolveBlockStyle  pure function: style + context → inline CSS
 *
 * resolveBlockStyle is shared by the editor wrapper
 * (canvas/adminBlockWrapper.vue) and the public renderer
 * (Website/vue/SiteBlockRenderer.vue). Both MUST use it so the
 * editor and the published site always match.
 *
 * No Vue imports — safe for Jest and for the PHP-side mirror.
 */

import { cssBackgroundImage } from '../shared/blockUtils.js';

// ── Trait catalog ──────────────────────────────────────────────────────────

import { createDefaultAudience, describeAudience } from '../shared/audienceRules.js';

export var TRAIT_ORDER = [
	'layout',
	'spacing',
	'background',
	'border',
	'typography',
	'visibility',
	'advanced'
];

export var TRAIT_DEFS = {
	layout: {
		label: 'Layout & Width',
		icon: 'arrows-h',
		keywords: 'layout width display inline align alignment full auto'
	},
	spacing: {
		label: 'Spacing',
		icon: 'arrows-v',
		keywords: 'spacing padding margin gap tight roomy spacious mobile'
	},
	background: {
		label: 'Background',
		icon: 'paint-brush',
		keywords: 'background color image overlay'
	},
	border: {
		label: 'Border & Radius',
		icon: 'square-o',
		keywords: 'border radius corner shadow outline'
	},
	typography: {
		label: 'Typography',
		icon: 'font',
		keywords: 'font typography text size weight color family transform'
	},
	visibility: {
		label: 'Visibility',
		icon: 'eye',
		keywords: 'visibility hide show desktop mobile audience members membership newsletter subscribers signed in logged in'
	},
	advanced: {
		label: 'Advanced',
		icon: 'code',
		keywords: 'advanced css class anchor id custom padding margin pixel'
	}
};

/** Every trait, in order. Used when a definition omits `traits`. */
export var ALL_TRAITS = TRAIT_ORDER.slice();

/**
 * Returns the enabled traits for a block definition, in fixed order.
 * Unknown names are dropped; order in the definition is ignored.
 *
 * @param {Object|null} definition  Entry from blockDefinitions.json
 * @returns {string[]}
 */
export function getEnabledTraits(definition) {
	var wanted = definition && Array.isArray(definition.traits) ? definition.traits : ALL_TRAITS;
	return TRAIT_ORDER.filter(function (t) { return wanted.indexOf(t) !== -1; });
}

// ── Tokens ─────────────────────────────────────────────────────────────────

/**
 * Five step spacing scale. Values are CSS lengths applied uniformly.
 * `mobile` is used when the viewport is mobile and no explicit mobile
 * token is set.
 */
export var SPACING_SCALE = {
	none:     { label: 'None',     desktop: '0',    mobile: '0' },
	tight:    { label: 'Tight',    desktop: '1rem', mobile: '0.75rem' },
	normal:   { label: 'Normal',   desktop: '2rem', mobile: '1.25rem' },
	roomy:    { label: 'Roomy',    desktop: '4rem', mobile: '2rem' },
	spacious: { label: 'Spacious', desktop: '6rem', mobile: '3rem' }
};

export var SPACING_TOKENS = Object.keys(SPACING_SCALE);

export var RADIUS_SCALE = {
	none: { label: 'None',  value: '0' },
	sm:   { label: 'Small', value: '4px' },
	md:   { label: 'Medium', value: '8px' },
	lg:   { label: 'Large', value: '16px' },
	pill: { label: 'Pill',  value: '999px' }
};

export var SHADOW_SCALE = {
	none: { label: 'None',   value: '' },
	sm:   { label: 'Small',  value: '0 1px 4px rgba(0,0,0,0.08)' },
	md:   { label: 'Medium', value: '0 4px 12px rgba(0,0,0,0.1)' },
	lg:   { label: 'Large',  value: '0 8px 24px rgba(0,0,0,0.14)' }
};

export var WIDTH_MODES = [
	{ value: '',        label: 'Auto' },
	{ value: 'fixed',   label: 'Fixed px' },
	{ value: 'percent', label: 'Percent' },
	{ value: 'min',     label: 'Min px' }
];

export var DISPLAY_MODES = [
	{ value: 'block',        label: 'Block',  icon: 'square-o' },
	{ value: 'inline-block', label: 'Inline', icon: 'ellipsis-h' }
];

export var ALIGN_OPTIONS = [
	{ value: 'left',   label: 'Left',   icon: 'align-left' },
	{ value: 'center', label: 'Center', icon: 'align-center' },
	{ value: 'right',  label: 'Right',  icon: 'align-right' }
];

export var VISIBILITY_OPTIONS = [
	{ value: 'always',      label: 'All',     icon: 'eye' },
	{ value: 'desktopOnly', label: 'Desktop', icon: 'desktop' },
	{ value: 'mobileOnly',  label: 'Mobile',  icon: 'mobile' }
];

export var THEME_OPTIONS = ['', 'light', 'dark'];

/** Fallback theme tokens when the site has none configured. */
var THEME_FALLBACK = {
	light: { bg: '#FFFFFF', text: '#1E2031', accent: '#FB6A00' },
	dark:  { bg: '#1E2031', text: '#F4F4F5', accent: '#FB6A00' }
};

// ── Default style ──────────────────────────────────────────────────────────

/**
 * A complete, empty style object. Every trait key exists so templates
 * can bind without null checks. Values are tokens or '' (inherit).
 *
 * @returns {Object}
 */
export function createDefaultStyle() {
	return {
		layout: {
			widthMode: '',        // '' | fixed | percent | min
			widthValue: '',       // number as string
			display: 'block',     // block | inline-block
			align: '',            // '' | left | center | right  (text-align / self alignment)
			mobile: { widthMode: '', widthValue: '', display: '' }
		},
		spacing: {
			padding: '',          // spacing token or ''
			margin: '',           // spacing token or ''
			mobile: { padding: '', margin: '' }
		},
		background: {
			theme: '',            // '' | light | dark
			color: '',
			image: '',
			size: 'cover',
			position: 'center center',
			repeat: 'no-repeat',
			overlay: '',
			overlayOpacity: ''
		},
		border: {
			sides: null,          // { desktop: {top,right,bottom,left}, mobile: {...} } or flat {top,...} | null
			radius: '',           // radius token or css length
			shadow: ''            // shadow token | '' | {x,y,blur,spread,color}
		},
		typography: {
			fontFamily: '',
			fontSize: '',
			fontWeight: '',
			fontStyle: '',
			textDecoration: '',
			textTransform: '',
			lineHeight: '',
			letterSpacing: '',
			color: '',
			textAlign: ''
		},
		visibility: 'always',
		audience: createDefaultAudience(),
		advanced: {
			cssClass: '',
			anchorId: '',
			padding: '',          // raw CSS shorthand, beats the token
			margin: '',
			mobile: { padding: '', margin: '' }
		}
	};
}

/**
 * Deep-merges a partial style over the defaults so every key exists.
 * Legacy flat `_wrapper*` props are migrated when `legacyProps` is given.
 *
 * @param {Object|null} style
 * @param {Object|null} [legacyProps]
 * @returns {Object}
 */
export function normalizeStyle(style, legacyProps) {
	var out = createDefaultStyle();
	if (legacyProps && typeof legacyProps === 'object') {
		migrateLegacyWrapperProps(legacyProps, out);
	}
	if (style && typeof style === 'object') {
		deepAssign(out, style);
	}
	return out;
}

function deepAssign(target, source) {
	var keys = Object.keys(source);
	for (var i = 0; i < keys.length; i++) {
		var k = keys[i];
		var v = source[k];
		if (v && typeof v === 'object' && !Array.isArray(v) && target[k] && typeof target[k] === 'object' && !Array.isArray(target[k])) {
			deepAssign(target[k], v);
		} else if (v !== undefined) {
			target[k] = v;
		}
	}
	return target;
}

/**
 * Maps the old flat `_wrapper*` props and the old top-level `visibility`
 * prop onto the trait model. Mutates `out`. Returns the list of legacy
 * keys consumed so callers can strip them from props.
 *
 * @param {Object} p    legacy props
 * @param {Object} out  style object to fill
 * @returns {string[]}
 */
export function migrateLegacyWrapperProps(p, out) {
	var used = [];
	function take(key) { if (p[key] !== undefined) { used.push(key); return p[key]; } return undefined; }

	var v;
	if ((v = take('_wrapperTheme')) !== undefined) out.background.theme = v || '';
	if ((v = take('_wrapperBg')) !== undefined) out.background.color = v || '';
	if ((v = take('_wrapperBgImage')) !== undefined) out.background.image = v || '';
	if ((v = take('_wrapperBgSize')) !== undefined && v) out.background.size = v;
	if ((v = take('_wrapperBgPosition')) !== undefined && v) out.background.position = v;
	if ((v = take('_wrapperBgRepeat')) !== undefined && v) out.background.repeat = v;
	if ((v = take('_wrapperBgOverlay')) !== undefined) out.background.overlay = v || '';
	if ((v = take('_wrapperBgOverlayOpacity')) !== undefined) out.background.overlayOpacity = v || '';

	if ((v = take('_wrapperPadding')) !== undefined) out.advanced.padding = v || '';
	if ((v = take('_wrapperMobilePadding')) !== undefined) out.advanced.mobile.padding = v || '';
	var mt = take('_wrapperMarginTop'), mr = take('_wrapperMarginRight'), mb = take('_wrapperMarginBottom'), ml = take('_wrapperMarginLeft');
	if (mt || mr || mb || ml) out.advanced.margin = [mt || '0', mr || '0', mb || '0', ml || '0'].join(' ');
	var mmt = take('_wrapperMobileMarginTop'), mmr = take('_wrapperMobileMarginRight'), mmb = take('_wrapperMobileMarginBottom'), mml = take('_wrapperMobileMarginLeft');
	if (mmt || mmr || mmb || mml) out.advanced.mobile.margin = [mmt || '0', mmr || '0', mmb || '0', mml || '0'].join(' ');

	if ((v = take('_wrapperBorderRadius')) !== undefined) out.border.radius = v || '';
	if ((v = take('_wrapperBorderSides')) !== undefined) out.border.sides = v || null;
	var bw = take('_wrapperBorderWidth'), bc = take('_wrapperBorderColor');
	if (bw && !out.border.sides) {
		var side = { width: parseFloat(bw) || 1, style: 'solid', color: bc || '#DDDEE0' };
		out.border.sides = { top: side, right: side, bottom: side, left: side };
	}
	if ((v = take('_wrapperShadow')) !== undefined) out.border.shadow = v || '';

	if ((v = take('_wrapperWidthMode')) !== undefined) out.layout.widthMode = v || '';
	if ((v = take('_wrapperWidthValue')) !== undefined) out.layout.widthValue = v || '';
	if ((v = take('_wrapperDisplay')) !== undefined) out.layout.display = v || 'block';
	if ((v = take('_wrapperMobileWidthMode')) !== undefined) out.layout.mobile.widthMode = v || '';
	if ((v = take('_wrapperMobileWidthValue')) !== undefined) out.layout.mobile.widthValue = v || '';
	if ((v = take('_wrapperMobileDisplay')) !== undefined) out.layout.mobile.display = v || '';

	if ((v = take('_wrapperCssClass')) !== undefined) out.advanced.cssClass = v || '';
	if ((v = take('visibility')) !== undefined) out.visibility = v || 'always';

	return used;
}

// ── Style resolver ─────────────────────────────────────────────────────────

var BORDER_SIDES = ['top', 'right', 'bottom', 'left'];
var BORDER_CSS = { top: 'borderTop', right: 'borderRight', bottom: 'borderBottom', left: 'borderLeft' };

/**
 * Picks the side set for the viewport. Accepts the responsive shape
 * { desktop, mobile } or a flat { top, right, bottom, left }.
 * Mobile falls back to desktop when no mobile sides are set.
 */
function resolveBorderSides(sides, isMobile) {
	if (!sides || typeof sides !== 'object') return null;
	if (sides.desktop !== undefined || sides.mobile !== undefined) {
		if (isMobile && sides.mobile && hasAnyBorder(sides.mobile)) return sides.mobile;
		return sides.desktop || null;
	}
	return sides;
}

function hasAnyBorder(sideSet) {
	if (!sideSet) return false;
	for (var i = 0; i < BORDER_SIDES.length; i++) {
		var sd = sideSet[BORDER_SIDES[i]];
		if (sd && sd.width > 0) return true;
	}
	return false;
}

/**
 * Resolves a spacing value: raw CSS from advanced wins, then token.
 */
function resolveSpacing(style, key, isMobile) {
	var adv = style.advanced || {};
	var sp = style.spacing || {};
	if (isMobile) {
		if (adv.mobile && adv.mobile[key]) return adv.mobile[key];
		if (sp.mobile && sp.mobile[key] && SPACING_SCALE[sp.mobile[key]]) return SPACING_SCALE[sp.mobile[key]].mobile;
	}
	if (adv[key]) return adv[key];
	if (sp[key] && SPACING_SCALE[sp[key]]) {
		return isMobile ? SPACING_SCALE[sp[key]].mobile : SPACING_SCALE[sp[key]].desktop;
	}
	return '';
}

/**
 * Resolves trait values into an inline style object for the block
 * wrapper element. Pure; no DOM access.
 *
 * @param {Object} style         Block style (normalized or partial)
 * @param {Object} [ctx]
 * @param {boolean} [ctx.isMobile]      Current viewport is mobile
 * @param {Object}  [ctx.siteSettings]  Site settings for theme tokens
 * @param {boolean} [ctx.skipBorder]    Container blocks that paint their own border
 * @returns {{ style: Object, cssClass: string, hasOverlay: boolean, overlayStyle: Object|null }}
 */
export function resolveBlockStyle(style, ctx) {
	ctx = ctx || {};
	var st = normalizeStyle(style);
	var isMobile = !!ctx.isMobile;
	var ss = ctx.siteSettings || {};
	var s = {};

	// ── Background & theme ──
	var bg = st.background;
	if (bg.theme === 'light' || bg.theme === 'dark') {
		var prefix = bg.theme === 'dark' ? 'darkTheme' : 'lightTheme';
		var fb = THEME_FALLBACK[bg.theme];
		var themeText = ss[prefix + 'Text'] || fb.text;
		var themeAccent = ss[prefix + 'Accent'] || fb.accent;
		s.backgroundColor = ss[prefix + 'Bg'] || fb.bg;
		s.color = themeText;
		s['--theme-accent'] = themeAccent;
		// Blocks read text colors from the site variables declared on the page
		// root. Re-declare them here so the theme cascades into nested blocks.
		s['--site-heading-color'] = themeText;
		s['--site-body-color'] = themeText;
		s['--site-h1-color'] = themeText;
		s['--site-h2-color'] = themeText;
		s['--site-h3-color'] = themeText;
		s['--site-h4-color'] = themeText;
		s['--site-h5-color'] = themeText;
		s['--site-h6-color'] = themeText;
		s['--site-link-color'] = themeAccent;
		s['--site-accent-color'] = themeAccent;
	}
	if (bg.color) s.backgroundColor = bg.color;
	var hasOverlay = !!(bg.image && bg.overlay);
	if (bg.image) {
		s.backgroundImage = cssBackgroundImage(bg.image);
		s.backgroundSize = bg.size || 'cover';
		s.backgroundPosition = bg.position || 'center center';
		s.backgroundRepeat = bg.repeat || 'no-repeat';
		if (hasOverlay) {
			s.position = 'relative';
			s.overflow = 'hidden';
		}
	}

	// ── Spacing ──
	var pad = resolveSpacing(st, 'padding', isMobile);
	var mar = resolveSpacing(st, 'margin', isMobile);
	if (pad) s.padding = pad;
	if (mar) s.margin = mar;

	// ── Border & radius & shadow ──
	var bd = st.border;
	if (bd.radius) {
		s.borderRadius = RADIUS_SCALE[bd.radius] ? RADIUS_SCALE[bd.radius].value : bd.radius;
		// Media blocks read this so their inner element gets the same corners.
		s['--block-radius'] = s.borderRadius;
	}
	var sides = resolveBorderSides(bd.sides, isMobile);
	if (!ctx.skipBorder && sides) {
		for (var i = 0; i < BORDER_SIDES.length; i++) {
			var sd = sides[BORDER_SIDES[i]];
			if (sd && sd.width > 0) {
				s[BORDER_CSS[BORDER_SIDES[i]]] = sd.width + 'px ' + (sd.style || 'solid') + ' ' + (sd.color || '#DDDEE0');
			}
		}
	}
	if (bd.shadow) {
		if (typeof bd.shadow === 'object') {
			var sh = bd.shadow;
			s.boxShadow = (sh.x || 0) + 'px ' + (sh.y || 0) + 'px ' + (sh.blur || 0) + 'px ' + (sh.spread || 0) + 'px ' + (sh.color || 'rgba(0,0,0,0.1)');
		} else if (SHADOW_SCALE[bd.shadow] && SHADOW_SCALE[bd.shadow].value) {
			s.boxShadow = SHADOW_SCALE[bd.shadow].value;
		}
	}

	// ── Typography (inherited by block content) ──
	var ty = st.typography;
	// Each value also overrides the per-level heading variables, so a heading inside
	// this block follows the block's Typography trait instead of the site tokens
	// (HeadingBlock resolves --site-hN-* first, then --site-heading-*, then inherit).
	var headingVars = function (suffix, value) {
		for (var lvl = 1; lvl <= 6; lvl++) s['--site-h' + lvl + '-' + suffix] = value;
	};
	if (ty.fontFamily) { s.fontFamily = ty.fontFamily; headingVars('font', ty.fontFamily); }
	if (ty.fontSize) { s.fontSize = ty.fontSize; headingVars('size', ty.fontSize); }
	if (ty.fontWeight) { s.fontWeight = ty.fontWeight; headingVars('weight', ty.fontWeight); }
	if (ty.fontStyle) { s.fontStyle = ty.fontStyle; headingVars('style', ty.fontStyle); }
	if (ty.textDecoration) { s.textDecoration = ty.textDecoration; headingVars('decoration', ty.textDecoration); }
	if (ty.textTransform) { s.textTransform = ty.textTransform; headingVars('transform', ty.textTransform); }
	if (ty.lineHeight) { s.lineHeight = ty.lineHeight; headingVars('line-height', ty.lineHeight); }
	if (ty.letterSpacing) { s.letterSpacing = ty.letterSpacing; headingVars('letter-spacing', ty.letterSpacing); }
	if (ty.color) {
		// One text color for everything inside: body, headings and links
		// (the site tokens would otherwise beat the inherited color).
		s.color = ty.color;
		s['--site-heading-color'] = ty.color;
		s['--site-body-color'] = ty.color;
		s['--site-link-color'] = ty.color;
		for (var hi = 1; hi <= 6; hi++) s['--site-h' + hi + '-color'] = ty.color;
	}
	if (ty.textAlign) s.textAlign = ty.textAlign;

	// ── Layout & width ──
	var lo = st.layout;
	var mo = (isMobile && lo.mobile) || {};
	var widthMode = (isMobile && mo.widthMode) || lo.widthMode || '';
	var widthValue = (isMobile && mo.widthValue) || lo.widthValue || '';
	var display = (isMobile && mo.display) || lo.display || 'block';

	if (widthMode === 'fixed' && widthValue) {
		s.width = widthValue + 'px';
		s.flexShrink = '0';
	} else if (widthMode === 'min' && widthValue) {
		s.minWidth = widthValue + 'px';
	} else if (widthMode === 'percent' && widthValue) {
		s.width = Math.min(parseFloat(widthValue) || 100, 100) + '%';
	}
	if (display === 'inline-block') {
		s.display = 'inline-block';
		s.verticalAlign = 'top';
	}
	if (lo.align) {
		s.textAlign = lo.align;
		// Center/right align a constrained-width block inside its parent
		if (widthMode && display !== 'inline-block') {
			if (lo.align === 'center') { s.marginLeft = 'auto'; s.marginRight = 'auto'; }
			else if (lo.align === 'right') { s.marginLeft = 'auto'; }
		}
	}

	// Containment: a block never exceeds its parent cell
	s.maxWidth = '100%';

	var overlayStyle = null;
	if (hasOverlay) {
		var op = parseFloat(bg.overlayOpacity);
		if (isNaN(op)) op = 0.5;
		overlayStyle = {
			position: 'absolute', top: '0', left: '0', right: '0', bottom: '0',
			backgroundColor: bg.overlay,
			opacity: String(op),
			pointerEvents: 'none',
			zIndex: 0,
			borderRadius: 'inherit'
		};
	}

	return {
		style: s,
		cssClass: (st.advanced && st.advanced.cssClass) || '',
		anchorId: (st.advanced && st.advanced.anchorId) || '',
		hasOverlay: hasOverlay,
		overlayStyle: overlayStyle,
		visibility: st.visibility || 'always',
		audience: st.audience || createDefaultAudience()
	};
}

var BAND_KEYS = ['backgroundColor', 'backgroundImage', 'backgroundSize', 'backgroundPosition', 'backgroundRepeat'];

/**
 * A root block with Full width on is a "band": its Background trait paints
 * the section edge to edge while the content stays at the page width.
 * @param {Object} block
 * @returns {boolean}
 */
export function isBandBlock(block) {
	var p = (block && block.props) || {};
	return p.fullWidth === true || p.fullWidth === 'true' || p.sectionFullWidth === true || p.sectionFullWidth === 'true';
}

/** A band whose content also runs edge to edge (no page-width cap). */
export function isStretchedBand(block) {
	var p = (block && block.props) || {};
	return isBandBlock(block) && (p.stretchContent === true || p.stretchContent === 'true');
}

/**
 * Splits a resolveBlockStyle() result for a band: background keys for the
 * outer section, the rest for the block wrapper.
 * @param {Object} resolved  resolveBlockStyle() result
 * @returns {{outer: Object, inner: Object}}
 */
export function splitBandStyle(resolved) {
	var outer = {}, inner = {};
	var s = (resolved && resolved.style) || {};
	Object.keys(s).forEach(function (k) {
		if (BAND_KEYS.indexOf(k) !== -1) outer[k] = s[k]; else inner[k] = s[k];
	});
	return { outer: outer, inner: inner };
}

/**
 * Reads a nested style value by dotted path, e.g. 'spacing.padding'.
 * @param {Object} style
 * @param {string} path
 * @returns {*}
 */
export function getStyleValue(style, path) {
	var parts = path.split('.');
	var cur = style;
	for (var i = 0; i < parts.length; i++) {
		if (cur === null || cur === undefined) return undefined;
		cur = cur[parts[i]];
	}
	return cur;
}

/**
 * Returns a new style object with the value at a dotted path replaced.
 * Does not mutate the input.
 * @param {Object} style
 * @param {string} path
 * @param {*} value
 * @returns {Object}
 */
export function setStyleValue(style, path, value) {
	var out = normalizeStyle(style);
	var parts = path.split('.');
	var cur = out;
	for (var i = 0; i < parts.length - 1; i++) {
		if (!cur[parts[i]] || typeof cur[parts[i]] !== 'object') cur[parts[i]] = {};
		cur = cur[parts[i]];
	}
	cur[parts[parts.length - 1]] = value;
	return out;
}

// ── Summaries (rail card headers) ─────────────────────────────────────────

function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

/**
 * One-line, human summary of a trait's current value, plus whether it
 * differs from the defaults. Drives the rail card header and glyph state.
 *
 * @param {string} trait
 * @param {Object} style   normalized block style
 * @returns {{ text: string, changed: boolean }}
 */
export function summarizeTrait(trait, style) {
	var st = normalizeStyle(style);
	var d = createDefaultStyle();
	var parts = [];
	switch (trait) {
		case 'layout': {
			var lo = st.layout;
			if (lo.align) parts.push(cap(lo.align));
			if (lo.widthMode === 'fixed' && lo.widthValue) parts.push(lo.widthValue + 'px');
			else if (lo.widthMode === 'percent' && lo.widthValue) parts.push(lo.widthValue + '%');
			else if (lo.widthMode === 'min' && lo.widthValue) parts.push('min ' + lo.widthValue + 'px');
			if (lo.display === 'inline-block') parts.push('Inline');
			return { text: parts.join(' · ') || 'Auto', changed: parts.length > 0 };
		}
		case 'spacing': {
			var sp = st.spacing;
			var pad = sp.padding ? SPACING_SCALE[sp.padding].label : null;
			var mar = sp.margin ? SPACING_SCALE[sp.margin].label : null;
			if (pad) parts.push('Pad ' + pad);
			if (mar) parts.push('Margin ' + mar);
			if (sp.mobile && (sp.mobile.padding || sp.mobile.margin)) parts.push('Mobile set');
			return { text: parts.join(' · ') || 'Default', changed: parts.length > 0 };
		}
		case 'background': {
			var bg = st.background;
			if (bg.theme) parts.push(cap(bg.theme) + ' theme');
			if (bg.color) parts.push(bg.color);
			if (bg.image) parts.push('Image');
			return { text: parts.join(' · ') || 'None', changed: parts.length > 0 };
		}
		case 'border': {
			var bd = st.border;
			if (bd.radius) parts.push(RADIUS_SCALE[bd.radius] ? RADIUS_SCALE[bd.radius].label + ' radius' : bd.radius);
			var sides = resolveBorderSides(bd.sides, false);
			if (sides && hasAnyBorder(sides)) parts.push('Border');
			if (bd.shadow) parts.push(typeof bd.shadow === 'object' ? 'Custom shadow' : SHADOW_SCALE[bd.shadow].label + ' shadow');
			return { text: parts.join(' · ') || 'None', changed: parts.length > 0 };
		}
		case 'typography': {
			var ty = st.typography;
			if (ty.fontFamily) parts.push(ty.fontFamily.split(',')[0].replace(/['"]/g, ''));
			if (ty.fontSize) parts.push(ty.fontSize);
			if (ty.fontWeight) parts.push(ty.fontWeight);
			if (ty.color) parts.push(ty.color);
			if (ty.textAlign) parts.push(cap(ty.textAlign));
			return { text: parts.join(' · ') || 'Site styles', changed: parts.length > 0 };
		}
		case 'visibility': {
			var map = { always: 'All devices', desktopOnly: 'Desktop only', mobileOnly: 'Mobile only' };
			var who = describeAudience(st.audience);
			var txt = who ? (st.visibility !== 'always' ? map[st.visibility] + ' · ' + who : who) : (map[st.visibility] || map.always);
			return { text: txt, changed: st.visibility !== d.visibility || !!who };
		}
		case 'advanced': {
			var a = st.advanced;
			if (a.cssClass) parts.push('.' + a.cssClass);
			if (a.anchorId) parts.push('#' + a.anchorId);
			if (a.padding || a.margin || (a.mobile && (a.mobile.padding || a.mobile.margin))) parts.push('Custom spacing');
			return { text: parts.join(' · ') || 'None', changed: parts.length > 0 };
		}
		default:
			return { text: '', changed: false };
	}
}

/**
 * Returns a fresh default value for one trait (used by per-card Reset).
 * @param {string} trait
 * @returns {*}
 */
export function defaultTraitValue(trait) {
	return createDefaultStyle()[trait];
}
